import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiFileList3Line } from '@remixicon/react';
import { CONNECTOR_LOG_LEVELS, type ConnectorDto, type ConnectorLogEntry, type ConnectorLogLevel } from '@atnt/shared';
import { Badge, DataTable, EmptyState, SegmentedControl, type BadgeColor, type DataTableColumn } from '@/shared/ui';
import { ErrorBanner, NoResults, RegistryToolbar } from '@/features/devices/components/parts';
import { formatReadingTime, matches } from '@/features/devices/format';
import { useHotkey } from '@/features/settings/hotkeys';
import { useTableDensity } from '@/features/settings/preferences';
import type { ConnectorsContext } from '../backend';
import { useConnectorLog } from '../api';
import { useRelativeTime } from './parts';

const LEVEL_COLOR: Record<ConnectorLogLevel, BadgeColor> = { info: 'green', warning: 'yellow', error: 'red' };

/** Translated log line: `log.codes.<code>` with the entry's params, the raw message as fallback. */
export function useLogMessage() {
  const { t } = useTranslation('connectors');
  return (e: ConnectorLogEntry) => (e.code ? t(`log.codes.${e.code}`, { ...e.params, defaultValue: e.message }) : e.message);
}

/** "Jurnal": sync / test / change events of the connector, newest first, filterable by level. */
export function LogTab({ c, ctx }: { c: ConnectorDto; ctx: ConnectorsContext }) {
  const { t } = useTranslation('connectors');
  const density = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState<'all' | ConnectorLogLevel>('all');
  const log = useConnectorLog(ctx, c.id);
  const message = useLogMessage();
  const rel = useRelativeTime();
  const all = log.data ?? [];
  const rows = all.filter((e) => (level === 'all' || e.level === level) && matches(message(e), search));

  useHotkey('search', () => searchRef.current?.focus());
  useHotkey('refresh', () => void log.refetch());

  const columns: DataTableColumn<ConnectorLogEntry>[] = [
    {
      id: 'at',
      header: t('log.time'),
      width: 260,
      cell: (e) => (
        <span className="flex min-w-0 items-baseline gap-8">
          <span className="tabular-nums">{formatReadingTime(e.at)}</span>
          <span className="truncate text-xs text-fg-secondary">{rel(e.at)}</span>
        </span>
      ),
    },
    { id: 'level', header: t('log.level'), width: 160, cell: (e) => <Badge color={LEVEL_COLOR[e.level]}>{t(`log.levels.${e.level}`)}</Badge> },
    { id: 'message', header: t('log.message'), tone: 'primary', cell: (e) => <span className="truncate">{message(e)}</span> },
  ];

  return (
    <>
      <RegistryToolbar search={search} onSearch={setSearch} placeholder={t('log.search')} total={log.data ? rows.length : null} searchRef={searchRef}>
        <SegmentedControl<'all' | ConnectorLogLevel>
          appearance="pill"
          aria-label={t('log.level')}
          value={level}
          onValueChange={setLevel}
          items={[
            { value: 'all', label: t('log.all') },
            ...CONNECTOR_LOG_LEVELS.map((l) => ({ value: l, label: t(`log.levels.${l}`), count: all.filter((e) => e.level === l).length })),
          ]}
        />
      </RegistryToolbar>
      {log.isError ? (
        <ErrorBanner onRetry={() => void log.refetch()} />
      ) : (
        <DataTable
          aria-label={t('detail.tabs.log')}
          columns={columns}
          rows={rows}
          getRowId={(e) => e.id}
          indexColumn={{ header: t('index'), format: (i) => `${i + 1}.` }}
          loading={log.isLoading}
          density={density}
          minWidth={900}
          empty={
            search || level !== 'all' ? (
              <NoResults
                onClear={() => {
                  setSearch('');
                  setLevel('all');
                }}
              />
            ) : (
              <EmptyState icon={RiFileList3Line} title={t('log.empty')} />
            )
          }
        />
      )}
    </>
  );
}
